import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';

/**
 * Folds raw events down into the daily tables Analytics reads.
 *
 * The page must never aggregate `activity_log` directly — at ~3M rows/day
 * across 1,000 stores that scan would be the slowest thing in the product, on
 * the same disk that serves checkout. This runs it once instead.
 *
 * Every statement is an UPSERT over a whole day, so a re-run is idempotent and
 * a backfill is just the same query with an older date. That matters more than
 * it sounds: these tables are derived, and being able to rebuild them without
 * ceremony is what makes them safe to change.
 */
@Injectable()
export class AnalyticsRollupService {
  private readonly logger = new Logger(AnalyticsRollupService.name);

  constructor(private readonly prisma: PrismaService) {}

  /** Yesterday, once it can no longer change. */
  @Cron('25 2 * * *')
  async rollupYesterday(): Promise<void> {
    const day = new Date(Date.now() - 24 * 60 * 60 * 1000);
    await this.rollupDay(day);
  }

  /**
   * Today so far, so the page is not blank until tomorrow.
   *
   * Every 15 minutes rather than continuously: an activity feed that is 15
   * minutes stale is fine for a page about weekly trends, and the alternative
   * is re-scanning the current partition on a loop.
   */
  @Cron('*/15 * * * *')
  async rollupToday(): Promise<void> {
    await this.rollupDay(new Date());
  }

  async rollupDay(day: Date): Promise<void> {
    const date = day.toISOString().slice(0, 10);

    for (const [name, run] of [
      ['activity', () => this.rollupActivity(date)],
      ['products', () => this.rollupProducts(date)],
      ['search', () => this.rollupSearch(date)],
      ['merchants', () => this.rollupMerchants(date)],
    ] as const) {
      try {
        await run();
      } catch (error) {
        // One rollup failing must not stop the others — a broken product
        // rollup should not also cost the platform its cost-to-serve numbers.
        this.logger.warn(
          `analytics rollup "${name}" failed for ${date}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
  }

  /**
   * Event counts, plus DISTINCT sessions and visitors.
   *
   * Sessions matter: 40 pageviews from one shopper is a completely different
   * fact from 40 shoppers, and a funnel built on raw event counts happily
   * reports conversion rates above 100%.
   */
  private async rollupActivity(date: string): Promise<void> {
    await this.prisma.$executeRawUnsafe(
      `
      INSERT INTO audit.activity_rollup_daily
        ("storeId", "day", "eventName", "eventCount", "sessionCount", "visitorCount", "updatedAt")
      SELECT "storeId", $1::date, "eventName",
             count(*)::int,
             count(DISTINCT "sessionId")::int,
             count(DISTINCT "anonymousId")::int,
             now()
        FROM audit.activity_log
       WHERE "receivedAt" >= $1::date AND "receivedAt" < $1::date + 1
         AND "storeId" IS NOT NULL
       GROUP BY "storeId", "eventName"
      ON CONFLICT ("storeId", "day", "eventName") DO UPDATE
        SET "eventCount"   = EXCLUDED."eventCount",
            "sessionCount" = EXCLUDED."sessionCount",
            "visitorCount" = EXCLUDED."visitorCount",
            "updatedAt"    = now()
      `,
      date
    );
  }

  /**
   * Views and add-to-carts come from behaviour; purchases and revenue come from
   * the ORDER table.
   *
   * That split is the whole point. A client-side "purchase" event cannot see a
   * later refund and is lost entirely to ad-blockers, so revenue taken from it
   * would never match the merchant's books — and merchants notice. Taking it
   * from `order_items` means this column reconciles.
   */
  private async rollupProducts(date: string): Promise<void> {
    await this.prisma.$executeRawUnsafe(
      `
      WITH behaviour AS (
        SELECT "storeId", "targetId" AS "productId",
               count(*) FILTER (WHERE "eventName" = 'product.view')::int AS views,
               count(*) FILTER (WHERE "eventName" = 'cart.add')::int     AS adds
          FROM audit.activity_log
         WHERE "receivedAt" >= $1::date AND "receivedAt" < $1::date + 1
           AND "storeId" IS NOT NULL AND "targetId" IS NOT NULL
           AND "eventName" IN ('product.view', 'cart.add')
         GROUP BY "storeId", "targetId"
      ),
      sold AS (
        SELECT o."storeId", oi."productId",
               count(*)::int AS purchases,
               COALESCE(sum(oi."lineTotal"), 0)::bigint AS revenue
          FROM sales.order_items oi
          JOIN sales.orders o ON o."id" = oi."orderId"
         WHERE o."createdOn" >= $1::date AND o."createdOn" < $1::date + 1
           AND oi."productId" IS NOT NULL
         GROUP BY o."storeId", oi."productId"
      )
      INSERT INTO audit.product_stats_daily
        ("storeId", "day", "productId", "views", "addToCarts", "purchases", "revenue", "updatedAt")
      SELECT COALESCE(b."storeId", s."storeId"),
             $1::date,
             COALESCE(b."productId", s."productId"),
             COALESCE(b.views, 0), COALESCE(b.adds, 0),
             COALESCE(s.purchases, 0), COALESCE(s.revenue, 0),
             now()
        FROM behaviour b
        FULL OUTER JOIN sold s
          ON s."storeId" = b."storeId" AND s."productId" = b."productId"
       WHERE COALESCE(b."storeId", s."storeId") IS NOT NULL
         AND COALESCE(b."productId", s."productId") IS NOT NULL
      ON CONFLICT ("storeId", "day", "productId") DO UPDATE
        SET "views" = EXCLUDED."views", "addToCarts" = EXCLUDED."addToCarts",
            "purchases" = EXCLUDED."purchases", "revenue" = EXCLUDED."revenue",
            "updatedAt" = now()
      `,
      date
    );
  }

  /**
   * Search terms, and how often each returned nothing.
   *
   * Terms are lower-cased and trimmed so "Kurta" and "kurta " are one row —
   * otherwise the ranking splits across spellings and buries the signal.
   */
  private async rollupSearch(date: string): Promise<void> {
    await this.prisma.$executeRawUnsafe(
      `
      INSERT INTO audit.search_rollup_daily
        ("storeId", "day", "term", "searchCount", "zeroResults", "updatedAt")
      SELECT "storeId", $1::date,
             lower(btrim("props"->>'q')),
             count(*)::int,
             count(*) FILTER (WHERE ("props"->>'resultCount')::int = 0)::int,
             now()
        FROM audit.activity_log
       WHERE "receivedAt" >= $1::date AND "receivedAt" < $1::date + 1
         AND "eventName" = 'search.submit'
         AND "storeId" IS NOT NULL
         AND btrim(COALESCE("props"->>'q', '')) <> ''
       GROUP BY "storeId", lower(btrim("props"->>'q'))
      ON CONFLICT ("storeId", "day", "term") DO UPDATE
        SET "searchCount" = EXCLUDED."searchCount",
            "zeroResults" = EXCLUDED."zeroResults",
            "updatedAt"   = now()
      `,
      date
    );
  }

  /**
   * The platform's own row per store per day.
   *
   * `apiCalls`/`apiErrors` come from `interface_log`, which is SAMPLED for
   * successful GETs — so apiCalls is scaled back up by `sampleRate` to
   * approximate real traffic. Without that the cost-to-serve number would
   * understate read-heavy merchants by ~100x, which is exactly the population
   * it is meant to identify.
   */
  private async rollupMerchants(date: string): Promise<void> {
    await this.prisma.$executeRawUnsafe(
      `
      WITH api AS (
        SELECT "storeId",
               COALESCE(sum("sampleRate"), 0)::int AS calls,
               count(*) FILTER (WHERE "statusCode" >= 500)::int AS errors
          FROM audit.interface_log
         WHERE "requestedOn" >= $1::date AND "requestedOn" < $1::date + 1
           AND "storeId" IS NOT NULL
         GROUP BY "storeId"
      ),
      commerce AS (
        SELECT "storeId", count(*)::int AS orders,
               COALESCE(sum("grandTotal"), 0)::bigint AS gmv
          FROM sales.orders
         WHERE "createdOn" >= $1::date AND "createdOn" < $1::date + 1
         GROUP BY "storeId"
      ),
      sessions AS (
        SELECT "storeId", count(DISTINCT "sessionId")::int AS sessions
          FROM audit.activity_log
         WHERE "receivedAt" >= $1::date AND "receivedAt" < $1::date + 1
           AND "storeId" IS NOT NULL
         GROUP BY "storeId"
      ),
      admin_work AS (
        SELECT "storeId", count(*)::int AS actions, count(*)::int AS audit_rows
          FROM audit.change_log
         WHERE "changedOn" >= $1::date AND "changedOn" < $1::date + 1
           AND "storeId" IS NOT NULL
         GROUP BY "storeId"
      ),
      stores AS (
        SELECT DISTINCT "storeId" FROM (
          SELECT "storeId" FROM api        UNION
          SELECT "storeId" FROM commerce   UNION
          SELECT "storeId" FROM sessions   UNION
          SELECT "storeId" FROM admin_work
        ) s WHERE "storeId" IS NOT NULL
      )
      INSERT INTO platform.merchant_stats_daily
        ("storeId", "organizationId", "day", "orders", "gmv", "sessions",
         "adminActions", "apiCalls", "apiErrors", "auditRows", "updatedAt")
      SELECT st."storeId",
             (SELECT "organizationId" FROM store.stores WHERE "id" = st."storeId"),
             $1::date,
             COALESCE(c.orders, 0), COALESCE(c.gmv, 0),
             COALESCE(se.sessions, 0), COALESCE(aw.actions, 0),
             COALESCE(a.calls, 0), COALESCE(a.errors, 0), COALESCE(aw.audit_rows, 0),
             now()
        FROM stores st
        LEFT JOIN api        a  ON a."storeId"  = st."storeId"
        LEFT JOIN commerce   c  ON c."storeId"  = st."storeId"
        LEFT JOIN sessions   se ON se."storeId" = st."storeId"
        LEFT JOIN admin_work aw ON aw."storeId" = st."storeId"
      ON CONFLICT ("storeId", "day") DO UPDATE
        SET "orders" = EXCLUDED."orders", "gmv" = EXCLUDED."gmv",
            "sessions" = EXCLUDED."sessions", "adminActions" = EXCLUDED."adminActions",
            "apiCalls" = EXCLUDED."apiCalls", "apiErrors" = EXCLUDED."apiErrors",
            "auditRows" = EXCLUDED."auditRows", "updatedAt" = now()
      `,
      date
    );
  }

  /** Rebuilds a range. Used for backfill, and after any change to the rollup shape. */
  async backfill(days: number): Promise<number> {
    let done = 0;

    for (let offset = days; offset >= 0; offset -= 1) {
      await this.rollupDay(new Date(Date.now() - offset * 24 * 60 * 60 * 1000));
      done += 1;
    }

    this.logger.log(`analytics backfill complete for ${done} day(s)`);
    return done;
  }

  /** Rollups outlive their sources on purpose — 13 months, so year-on-year works. */
  @Cron('40 3 * * 0')
  async purgeOldRollups(): Promise<void> {
    const cutoff = `now() - interval '13 months'`;

    for (const table of [
      'audit.activity_rollup_daily',
      'audit.product_stats_daily',
      'audit.search_rollup_daily',
      'platform.merchant_stats_daily',
    ]) {
      try {
        await this.prisma.$executeRawUnsafe(`DELETE FROM ${table} WHERE "day" < ${cutoff}`);
      } catch (error) {
        this.logger.warn(
          `rollup purge failed for ${table}: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }
  }
}
